import { useEffect, useState } from 'preact/hooks';
import type { ClickXY } from '@pinmerge/shared';
import { palette, colors, alpha } from '@pinmerge/tokens';
import { buildSelector, clickOffsetPctOf, resolveMeaningfulTarget, trimHtml } from '../capture';
import { avatarSrc, DEFAULT_AVATAR_BG, DEFAULT_AVATAR_ID } from '../components/AvatarSlot';
import type { DraftPin, Mode } from '../lib/types';
import type { HostAdapter } from '@pinmerge/shared';

interface UsePickerArgs {
  // True if picking is allowed (signed-in OR demoMode). When false and the user
  // attempts to start picking via a chord, we call `onNoAuth` instead.
  canPickWithoutLogin: boolean;
  // Whether picking should be blocked because something else is active (a pin
  // popover open, etc.). Chord shortcuts skip when this is true.
  idle: boolean;
  /** API base — used to build the avatar SVG src for the cursor-following preview pin. */
  apiUrl: string;
  /** Current reviewer's avatar so the cursor preview matches what the dropped pin
   *  will look like. Null in demo mode or before auth handoff completes — falls
   *  back to initials of `previewName`. */
  previewAvatar: { id: string | null; bg: string | null; name: string } | null;
  onNoAuth: () => void;
  onPick: (draft: DraftPin) => void;
  /** Host-application adapter. Accepted so App can thread it through; not
   *  consumed yet (wiring lands in a later WP). */
  adapter?: HostAdapter;
}

export interface UsePickerResult {
  mode: Mode;
  setMode: (m: Mode) => void;
  startPicking: () => void;
  stopPicking: () => void;
}

export function usePicker({
  canPickWithoutLogin,
  idle,
  apiUrl,
  previewAvatar,
  onNoAuth,
  onPick,
}: UsePickerArgs): UsePickerResult {
  const [mode, setMode] = useState<Mode>('idle');

  // keyboard: bare `C` enters picking mode (only when widget is idle and the
  // user isn't typing). Any modifier (Cmd/Ctrl/Alt/Shift) bails so we don't
  // shadow native shortcuts like Cmd+C copy. Input-field detection walks
  // `composedPath()` because the widget lives in a Shadow DOM — without that,
  // typing in the widget's own login/textarea inputs retargets to the host
  // and looks like a non-editing target.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (mode !== 'idle' || !idle) return;
      if (e.isComposing) return;
      if (e.key.toLowerCase() !== 'c') return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (isEditingTarget(e)) return;
      e.preventDefault();
      if (!canPickWithoutLogin) onNoAuth();
      else setMode('picking');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, idle, canPickWithoutLogin, onNoAuth]);

  // Picking mode: hover preview + click captures the target as a draft pin.
  // Defensive: if mode somehow flips to picking without a signed-in reviewer,
  // bail out and route the user to the login modal instead of leaving them
  // in a dead crosshair state.
  //
  // The `idle` gate keeps mode='picking' alive across compose: when a draft
  // pin opens its popover, the parent flips `idle` to false, the effect
  // cleans up (overlays + cursor pin disappear), and once compose closes the
  // effect re-runs to resume picking. Lets the reviewer drop several pins
  // in a row without re-entering pick mode.
  useEffect(() => {
    if (mode !== 'picking') return;
    if (!idle) return;
    if (!canPickWithoutLogin) {
      setMode('idle');
      onNoAuth();
      return;
    }
    // Hide the native cursor — the avatar pin below stands in for it, so the
    // reviewer sees a preview of the marker that's about to drop. We restore
    // the cursor on cleanup.
    document.body.style.cursor = 'none';

    const hoverBox = document.createElement('div');
    hoverBox.setAttribute('data-pinmerge-hover', 'box');
    hoverBox.style.cssText = [
      'position: fixed',
      'pointer-events: none',
      'z-index: 2147483646',
      `border: 2px dashed ${colors.pin.own}`,
      `background: ${alpha(colors.pin.own, 0.08)}`,
      'border-radius: 4px',
      'transition: all 60ms linear',
      'box-sizing: border-box',
      'display: none',
      'margin: 0',
      'padding: 0',
    ].join(';');

    const hoverLabel = document.createElement('div');
    hoverLabel.setAttribute('data-pinmerge-hover', 'label');
    hoverLabel.style.cssText = [
      'position: fixed',
      'pointer-events: none',
      'z-index: 2147483646',
      `background: ${colors.pin.own}`,
      `color: ${palette.white}`,
      'padding: 3px 8px',
      'border-radius: 4px',
      'font: 500 11px/1.2 ui-sans-serif, system-ui, sans-serif',
      'white-space: nowrap',
      'transition: all 60ms linear',
      'box-sizing: border-box',
      'display: none',
      'margin: 0',
      `box-shadow: 0 2px 4px ${alpha(palette.black, 0.2)}`,
    ].join(';');

    // Cursor-following preview pin — visually mirrors the dropped marker so the
    // reviewer sees what they're about to place. Centered on the cursor like a
    // real marker (which uses `anchor.vx - MARKER_SIZE/2`). Z-index sits above
    // hoverBox so the pin stays on top of the element outline.
    const PIN_SIZE = 28;
    const cursorPin = document.createElement('div');
    cursorPin.setAttribute('data-pinmerge-hover', 'cursor-pin');
    const pinBg = previewAvatar?.bg ?? DEFAULT_AVATAR_BG;
    cursorPin.style.cssText = [
      'position: fixed',
      'pointer-events: none',
      'z-index: 2147483647',
      `width: ${PIN_SIZE}px`,
      `height: ${PIN_SIZE}px`,
      'border-radius: 50% 50% 50% 4px',
      `background: ${pinBg}`,
      `border: 2px solid ${palette.white}`,
      `box-shadow: 0 4px 10px ${alpha(palette.black, 0.25)}`,
      'box-sizing: border-box',
      'display: none',
      'overflow: hidden',
      'align-items: center',
      'justify-content: center',
      `color: ${palette.white}`,
      'font: 600 11px/1 ui-sans-serif, system-ui, sans-serif',
      'letter-spacing: 0.02em',
    ].join(';');

    const pinAvatarId = previewAvatar?.id ?? DEFAULT_AVATAR_ID;
    const img = document.createElement('img');
    img.src = avatarSrc(apiUrl, pinAvatarId);
    img.alt = '';
    img.style.cssText = 'width: 100%; height: 100%; object-fit: cover; display: block;';
    // Avatar SVG can 404 (legacy slug, missing file) — swap to the default
    // SVG so the reviewer never sees a broken-image icon at the cursor.
    img.onerror = () => {
      img.onerror = null;
      img.src = avatarSrc(apiUrl, DEFAULT_AVATAR_ID);
    };
    cursorPin.appendChild(img);

    document.body.appendChild(hoverBox);
    document.body.appendChild(hoverLabel);
    document.body.appendChild(cursorPin);

    let lastResolved: HTMLElement | null = null;
    let rafId = 0;
    let scrollRafId = 0;

    const updateHover = (clientX: number, clientY: number) => {
      const stack = document.elementsFromPoint(clientX, clientY);
      const target = stack.find(
        (el): el is HTMLElement =>
          el instanceof HTMLElement &&
          !el.closest('#pinmerge-host') &&
          !el.hasAttribute('data-pinmerge-hover'),
      );
      if (!target) {
        hoverBox.style.display = 'none';
        hoverLabel.style.display = 'none';
        lastResolved = null;
        return;
      }
      const resolved = resolveMeaningfulTarget(target);
      if (resolved === lastResolved) return;
      lastResolved = resolved;

      const r = resolved.getBoundingClientRect();
      hoverBox.style.display = 'block';
      hoverBox.style.left = `${r.left}px`;
      hoverBox.style.top = `${r.top}px`;
      hoverBox.style.width = `${r.width}px`;
      hoverBox.style.height = `${r.height}px`;

      const tag = resolved.tagName.toLowerCase();
      const dc = resolved.getAttribute('data-component');
      hoverLabel.textContent = dc ? `${tag} · ${dc}` : tag;
      hoverLabel.style.display = 'block';
      const labelTop = r.top - 24 < 4 ? r.top + 4 : r.top - 24;
      hoverLabel.style.left = `${Math.max(4, r.left)}px`;
      hoverLabel.style.top = `${labelTop}px`;
    };

    const onMove = (e: MouseEvent) => {
      // Pin position updates synchronously — visible feedback on every move,
      // no rAF coalescing — otherwise the pin lags noticeably behind the
      // cursor on fast moves. The element hover-resolution (which is more
      // expensive) is still rAF-throttled below.
      cursorPin.style.display = 'flex';
      cursorPin.style.left = `${e.clientX - PIN_SIZE / 2}px`;
      cursorPin.style.top = `${e.clientY - PIN_SIZE / 2}px`;
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        updateHover(e.clientX, e.clientY);
      });
    };

    const onLeave = () => {
      cursorPin.style.display = 'none';
    };

    const onScroll = () => {
      // Coalesce a burst of scroll events into one box reposition per frame.
      if (scrollRafId || !lastResolved) return;
      scrollRafId = requestAnimationFrame(() => {
        scrollRafId = 0;
        if (!lastResolved) return;
        const r = lastResolved.getBoundingClientRect();
        hoverBox.style.left = `${r.left}px`;
        hoverBox.style.top = `${r.top}px`;
        hoverBox.style.width = `${r.width}px`;
        hoverBox.style.height = `${r.height}px`;
      });
    };

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || target.closest('#pinmerge-host')) return;
      e.preventDefault();
      e.stopPropagation();

      hoverBox.style.display = 'none';
      hoverLabel.style.display = 'none';
      cursorPin.style.display = 'none';

      const click: ClickXY = { x: e.clientX, y: e.clientY };
      const resolved = resolveMeaningfulTarget(target);
      const r = resolved.getBoundingClientRect();

      const draft: DraftPin = {
        docX: click.x + window.scrollX,
        docY: click.y + window.scrollY,
        clickOffsetPct: clickOffsetPctOf(r, click),
        rect: {
          x: Math.round(r.left),
          y: Math.round(r.top),
          w: Math.round(r.width),
          h: Math.round(r.height),
        },
        elementSelector: buildSelector(resolved),
        elementHtml: trimHtml(resolved.outerHTML),
        viewport: { w: window.innerWidth, h: window.innerHeight },
      };

      // Keep mode='picking' — parent flips `idle` to false while compose is
      // open, which gates this effect off. After submit/cancel the effect
      // resumes automatically.
      onPick(draft);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    document.addEventListener('click', onClick, { capture: true });

    return () => {
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('scroll', onScroll, { capture: true });
      document.removeEventListener('click', onClick, { capture: true });
      if (rafId) cancelAnimationFrame(rafId);
      if (scrollRafId) cancelAnimationFrame(scrollRafId);
      hoverBox.remove();
      hoverLabel.remove();
      cursorPin.remove();
    };
  }, [mode, idle, canPickWithoutLogin, onPick, onNoAuth, apiUrl, previewAvatar]);

  return {
    mode,
    setMode,
    startPicking: () => setMode('picking'),
    stopPicking: () => setMode('idle'),
  };
}

// Walks the event's composedPath() so inputs inside the widget's Shadow DOM
// are recognised. A plain `e.target` check sees only the shadow host on
// shadow-scoped events, which would let key presses inside the login modal /
// comment textarea slip through.
function isEditingTarget(e: KeyboardEvent): boolean {
  const path = e.composedPath();
  for (const node of path) {
    if (!(node instanceof HTMLElement)) continue;
    if (node.tagName === 'INPUT' || node.tagName === 'TEXTAREA' || node.tagName === 'SELECT') return true;
    if (node.isContentEditable) return true;
  }
  return false;
}
